import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth'

import { getEnv } from '@/config/env'

import { roleSchema, type Role } from './roles'
import { purgeResidualTokens } from './token-storage'

export interface AuthSession {
  userId: string
  email: string | null

  displayName: string

  role: Role | null
}

function roleFromClaims(claims: Record<string, unknown> | undefined): Role | null {
  const groups = claims?.['cognito:groups']

  if (!Array.isArray(groups)) {
    return null
  }

  return roleSchema.options.find((role) => groups.includes(role)) ?? null
}

function claimString(claims: Record<string, unknown> | undefined, key: string): string | null {
  const value = claims?.[key]
  return typeof value === 'string' && value !== '' ? value : null
}

export async function fetchSession(): Promise<AuthSession | null> {
  try {
    const [user, session] = await Promise.all([getCurrentUser(), fetchAuthSession()])

    const claims = session.tokens?.idToken?.payload as Record<string, unknown> | undefined

    const email = claimString(claims, 'email') ?? user.signInDetails?.loginId ?? null

    return {
      userId: user.userId,
      email,
      displayName: claimString(claims, 'name') ?? email ?? user.username,
      role: roleFromClaims(claims),
    }
  } catch {
    return null
  }
}

export async function signOutLocally(): Promise<void> {
  try {
    await signOut()
  } finally {
    purgeResidualTokens(getEnv().VITE_COGNITO_CLIENT_ID)
  }
}
